import { TokenExtension } from '@utils/extensions/token.extension.ts';
import { RealtimeConfig } from '@utils/types/editors/realtime.ts';

export type SessionSource = Pick<
  RealtimeConfig,
  'audioSource' | 'streamUrl' | 'language' | 'modelType'
>;
const memory = new Map<string, SessionSource>();
const key = (id: string) => {
  const user = TokenExtension.get().decoded;
  if (!user?.oid || !user?.uid) return null;
  return `live-source:${user?.oid}:${user?.uid}:${id}`;
};

// The source stays in this tab, scoped to its signed-in identity. It is never
// placed in shareable recovery URLs or persisted in a cross-tab local store.
export const rememberSessionSource = (id: string, source: SessionSource) => {
  const storageKey = key(id);
  if (!storageKey) return;
  const { audioSource, streamUrl, language, modelType } = source;
  const saved = { audioSource, streamUrl, language, modelType };
  memory.set(storageKey, saved);
  if (memory.size > 32) memory.delete(memory.keys().next().value!);
  try {
    sessionStorage.setItem(storageKey, JSON.stringify(saved));
  } catch {
    /* Preview remains available in memory. */
  }
};
export const readSessionSource = (id: string): SessionSource | null => {
  const storageKey = key(id);
  if (!storageKey) return null;
  try {
    const value = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
    return value &&
      (value.audioSource === 'stream' || value.audioSource === 'microphone') &&
      typeof value.modelType === 'string'
      ? value
      : memory.get(storageKey) || null;
  } catch {
    return memory.get(storageKey) || null;
  }
};
export const forgetSessionSource = (id: string) => {
  const storageKey = key(id);
  if (!storageKey) return;
  memory.delete(storageKey);
  try {
    sessionStorage.removeItem(storageKey);
  } catch {
    // The in-memory entry is already gone when browser storage is unavailable.
  }
};
export const streamUrlIsValid = (value: string) => {
  try {
    return ['http:', 'https:'].includes(new URL(value.trim()).protocol);
  } catch {
    return false;
  }
};

export const liveStartErrorKey = (code: unknown) => {
  switch (code) {
    case 'source_not_found':
      return 'live-source-not-found';
    case 'source_access_denied':
      return 'live-source-access-denied';
    case 'source_invalid':
    case 'invalid_source_url':
      return 'live-source-invalid';
    case 'source_not_allowed':
    case 'source_unavailable':
      return 'live-source-unavailable';
    case 'identity_required':
    case 'invalid_identity':
      return 'realtime-sign-in-again';
    case 'organization_realtime_token_required':
      return 'realtime-subscription-required';
    default:
      return 'live-start-error';
  }
};
